import React, { useMemo, useState } from "react";
import { Modal, Spin } from "antd";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import {
  getSelectedBillingCycle,
  normalizeBillingCycle,
  normalizeSelectedPlan,
  redirectToStripePackageCheckout,
} from "../../shared/billing";
import styles from "./UpgradePlanModal.module.css";

const STARTER_PLAN_CONTENT = {
  name: "Starter",
  monthlyPrice: 229,
  annualPrice: 124.5,
  annualBadge: "17% off",
  monthlyPlanName: "Starter Monthly",
  yearlyPlanName: "Starter Yearly",
  features: [
    "Multiple User Seats",
    "150 Monthly Credits",
    "Full AI Candidate Matching",
    "Pipeline Analytics",
    "Custom Dashboards",
  ],
};

const BILLING_CYCLES = {
  MONTHLY: "monthly",
  YEARLY: "yearly",
};

const safeNormalizePlan = (value = "") => {
  if (typeof normalizeSelectedPlan === "function") {
    return normalizeSelectedPlan(value);
  }

  if (typeof value !== "string") {
    return "";
  }

  return value.trim().replace(/^['"]+|['"]+$/g, "").toLowerCase();
};

export default function UpgradePlanModal({
  visible,
  selectedPlan,
  planData,
  onClose,
  title = "Upgrade Plan",
  ctaLabel = "Continue to Payment",
  featuresTitle = "Everything from Free, plus:",
  priceSuffix = "Per month",
  width = 604,
  onContinue,
}) {
  const [billingCycle, setBillingCycle] = useState(getSelectedBillingCycle());
  const [isSubmitting, setIsSubmitting] = useState(false);

  const normalizedSelectedPlan = safeNormalizePlan(selectedPlan) || "starter";
  const planContent = useMemo(() => {
    if (planData) {
      return {
        ...STARTER_PLAN_CONTENT,
        ...planData,
        annualBadge: planData?.annualBadge || STARTER_PLAN_CONTENT.annualBadge,
        yearlyPlanName:
          planData?.yearlyPlanName ||
          planData?.name ||
          STARTER_PLAN_CONTENT.yearlyPlanName,
        monthlyPlanName:
          planData?.monthlyPlanName ||
          planData?.name ||
          STARTER_PLAN_CONTENT.monthlyPlanName,
      };
    }

    return STARTER_PLAN_CONTENT;
  }, [planData]);

  const monthlyOption = {
    label: planContent.monthlyPlanName,
  };
  const yearlyOption = {
    label: planContent.yearlyPlanName,
    badge: planContent.annualBadge,
  };

  const displayPrice =
    billingCycle === BILLING_CYCLES.YEARLY
      ? planContent.annualPrice ?? planContent.monthlyPrice
      : planContent.monthlyPrice ?? planContent.annualPrice;
  const planFeatures = Array.isArray(planContent.features)
    ? planContent.features
    : [];

  const handleContinue = async () => {
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    if (onContinue) {
      try {
        await onContinue({
          selectedPlan: normalizedSelectedPlan,
          billingCycle,
          planContent,
        });
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    try {
      await redirectToStripePackageCheckout({
        selectedPlan: normalizedSelectedPlan,
        billingCycle,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      centered
      width={width}
      closable={false}
      destroyOnClose
      wrapClassName={styles.modalWrap}
      bodyStyle={{ padding: 0 }}
    >
      <div className={styles.modal}>
        <div className={styles.header}>
          <div className={styles.title}>{title}</div>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Close upgrade plan modal"
          >
            <CloseOutlined />
          </button>
        </div>

        <div className={styles.divider} />

        <div className={styles.toggleRow}>
          <button
            type="button"
            className={`${styles.toggleButton} ${
              billingCycle === BILLING_CYCLES.MONTHLY ? styles.toggleButtonActive : ""
            }`}
            onClick={() =>
              setBillingCycle(normalizeBillingCycle(BILLING_CYCLES.MONTHLY))
            }
            disabled={isSubmitting}
          >
            {monthlyOption.label}
          </button>

          <button
            type="button"
            className={`${styles.toggleButton} ${
              billingCycle === BILLING_CYCLES.YEARLY ? styles.toggleButtonActive : ""
            }`}
            onClick={() =>
              setBillingCycle(normalizeBillingCycle(BILLING_CYCLES.YEARLY))
            }
            disabled={isSubmitting}
          >
            <span>{yearlyOption.label}</span>
            {yearlyOption.badge ? (
              <span className={styles.badge}>{yearlyOption.badge}</span>
            ) : null}
          </button>
        </div>

        <div className={styles.planSection}>
          <div className={styles.planName}>{planContent.name}</div>
          <div className={styles.price}>${displayPrice}</div>
          <div className={styles.priceSubtext}>{priceSuffix}</div>
        </div>

        <div className={styles.divider} />

        <div className={styles.featuresSection}>
          <div className={styles.featuresTitle}>{featuresTitle}</div>
          <div className={styles.featuresList}>
            {planFeatures.map((feature) => (
              <div key={feature} className={styles.featureItem}>
                <CheckOutlined className={styles.featureIcon} />
                <span>{feature}</span>
              </div>
            ))}
          </div>
        </div>

        <button
          type="button"
          className={styles.ctaButton}
          onClick={handleContinue}
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <span className={styles.ctaButtonContent}>
              <Spin size="small" />
              <span>Loading...</span>
            </span>
          ) : (
            ctaLabel
          )}
        </button>
      </div>
    </Modal>
  );
}
